<template>
    <div class="flex gap-x-4 items-center">
        <p
            v-if="!hideTitle"
            v-text="$lang.get('Search results')"
            class="px-4 pb-4 text-2xl font-medium font-header"
        />

        <div v-if="availableModelClasses.length > 1">
            <label
                v-text="$lang.get('Filter by type')"
                class="text-sm font-medium font-header pl-1"
            />
            <div class="flex flex-wrap gap-2.5 pb-2 justify-center">
                <ModelClassTag
                    v-for="modelClass in availableModelClasses"
                    :key="modelClass"
                    :modelClass="modelClass"
                    :clickable="true"
                    @click.stop="$emit('toggleModelClass', modelClass)"
                    :faded="!selectedModelClasses.includes(modelClass)"
                />
            </div>
        </div>
    </div>

    <ReactiveHeightTransition>
        <div :class="[maxHeight, 'overflow-y-auto']" ref="results">
            <SearchResult
                v-for="result in resultsWithDisabled"
                @resultClicked="(model) => $emit('selectModel', model)"
                :disabled="result.disabled"
                :disabledMessage="result.disabledMessage"
                :key="`${result.id}-${result.modelClass}`"
                :result="result"
                :isLink="false"
                @keydown.tab="handleTabForResults"
                lineClamp="line-clamp-2"
                imageHeight="h-[7rem]"
                :small="smallResults"
            />
        </div>
        <p
            v-if="!loading && results.length === 0"
            class="text-center py-10 text-2xl font-header font-thin"
        >
            {{ $lang.get('No results found') }}
        </p>
    </ReactiveHeightTransition>
</template>

<script>
import SearchResult from '@/Components/Search/SearchResult.vue'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
import ModelClassTag from '@/Components/ModelComponents/ModelClassTag.vue'

export default {
    components: {
        SearchResult,
        ReactiveHeightTransition,
        ModelClassTag,
    },

    emits: ['toggleModelClass', 'selectModel'],

    expose: ['navigateToFirstResult'],

    props: {
        results: {
            type: Array,
            required: true,
        },
        loading: {
            type: Boolean,
            required: true,
        },
        availableModelClasses: {
            type: Array,
            required: true,
        },
        selectedModelClasses: {
            type: Array,
            required: true,
        },
        hideTitle: {
            type: Boolean,
            default: false,
        },
        maxHeight: {
            type: String,
            default: 'max-h-[500px]',
        },
        smallResults: {
            type: Boolean,
            default: false,
        },
        disabledModels: {
            type: Array,
            default: () => [],
            validator: (value) => {
                if (
                    !value.every(
                        (model) =>
                            typeof model === 'object' &&
                            Object.hasOwn(model, 'modelId') &&
                            Object.hasOwn(model, 'modelClass') &&
                            Object.hasOwn(model, 'message')
                    )
                ) {
                    console.error(
                        'The disabledModels prop must be an array of objects with the keys modelId, modelClass and message'
                    )
                    return false
                }
                return true
            },
        },
    },

    computed: {
        resultsWithDisabled() {
            return this.results.map((result) => {
                const disabledModel = this.disabledModels.find(
                    (model) =>
                        model.modelId === result.id &&
                        model.modelClass === result.modelClass
                )
                return {
                    ...result,
                    disabled: !!disabledModel,
                    disabledMessage: disabledModel ? disabledModel.message : '',
                }
            })
        },
    },

    methods: {
        /**
         * If the users presses shift + tab and is currently focusing the first result,
         * we focus the last result to not cause an issue with headlesss uis focus traps
         */
        handleTabForResults(event) {
            if (event.shiftKey && event.key === 'Tab') {
                const results = this.$refs.results.children
                if (document.activeElement === results[0]) {
                    event.preventDefault()
                    event.stopPropagation()
                    results[results.length - 1].focus()
                }
            }
        },

        navigateToFirstResult() {
            // Check if there are any results
            if (this.$refs.results && this.$refs.results.children.length > 0) {
                // Wait for the transition to finish, then focus on the first result
                this.$nextTick(() => {
                    const firstResult = Array.from(
                        this.$refs.results.children
                    ).find((element) => !element.disabled)

                    if (firstResult) {
                        firstResult.focus() // Make sure the element can be focused
                    }
                })
            }
        },
    },
}
</script>
